import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { api } from "@/lib/api";
import { MessageCircle, ArrowUpRight, Percent, Landmark, BadgeCheck, FileDown } from "lucide-react";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const steps = [
  ["01", "Enquiry", "Tell us the aircraft, budget and your profile. No obligation."],
  ["02", "Credit review", "Our finance partner reviews the application and supporting documents."],
  ["03", "Term sheet", "Indicative pricing, tenor and structure issued in writing."],
  ["04", "Documentation", "Facility, security and registration documents executed."],
  ["05", "Funding & delivery", "Funds released at closing. You take delivery of the aircraft."],
];

const faqs = [
  ["How much do I need upfront?", "30% of the aircraft value as a down payment. The remaining 70% is financed over an agreed term."],
  ["What does the rate look like?", "Indicative and subject to credit review, aircraft profile, jurisdiction and tenor. Confirmed in the term sheet."],
  ["Which aircraft qualify?", "Business jets, turboprops, trainers and helicopters — new and pre-owned. Each case is assessed individually."],
  ["Who applies?", "Individuals and corporates. Owner-operators, flight training organisations and fleet operators are all eligible."],
  ["Is there a penalty for early settlement?", "Terms vary by facility and are stated in the term sheet before you sign."],
];

function fmtUSD(n) {
  return "$" + Math.round(n).toLocaleString("en-US");
}

function downloadEstimatePDF({ price, years, rate, down, financed, monthly, total }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const navy = [10, 22, 38];
  const gold = [165, 133, 76];
  const grey = [107, 118, 132];

  // Header band
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 96, "F");
  doc.setDrawColor(200, 169, 126);
  doc.setLineWidth(1);
  doc.setFont("times", "normal");
  doc.setFontSize(20);
  doc.setTextColor(200, 169, 126);
  doc.text("JS AVIATION GROUP", 48, 46);
  doc.setFontSize(10);
  doc.setTextColor(210, 210, 215);
  doc.text("AIRCRAFT FINANCE LEASE  —  INDICATIVE ESTIMATE", 48, 66);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...grey);
  doc.text(`Prepared ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`, W - 48, 78, { align: "right" });

  doc.setDrawColor(...gold);
  doc.setLineWidth(1.2);
  doc.line(48, 130, W - 48, 130);

  doc.setFont("times", "normal");
  doc.setFontSize(13);
  doc.setTextColor(...grey);
  doc.text("ESTIMATED MONTHLY LEASE", 48, 158);
  doc.setFontSize(34);
  doc.setTextColor(...navy);
  doc.text(`${fmtUSD(monthly)} / month`, 48, 192);

  const rows = [
    ["Aircraft price", fmtUSD(price)],
    ["Down payment (30%)", fmtUSD(down)],
    ["Amount financed", fmtUSD(financed)],
    ["Lease term", `${years} years (${years * 12} monthly payments)`],
    ["Indicative rate", `${rate.toFixed(1)}% per annum`],
    ["Estimated monthly lease", `${fmtUSD(monthly)} / month`],
    ["Total cost over term", fmtUSD(total)],
  ];
  let y = 236;
  doc.setFont("helvetica", "normal");
  rows.forEach(([k, v], i) => {
    if (i % 2 === 0) { doc.setFillColor(246, 246, 247); doc.rect(48, y - 14, W - 96, 30, "F"); }
    doc.setFontSize(9);
    doc.setTextColor(...grey);
    doc.text(k.toUpperCase(), 60, y + 5);
    doc.setFontSize(12);
    doc.setTextColor(...navy);
    doc.text(String(v), W - 60, y + 5, { align: "right" });
    y += 30;
  });

  y += 24;
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.8);
  doc.line(48, y, W - 48, y);
  y += 20;
  doc.setFontSize(8);
  doc.setTextColor(...grey);
  const disclaimer = "Illustrative estimate only — not an offer of credit. Financing is provided by an independent third-party lender. JS Aviation Group acts as an introducer only. All offers are subject to credit review, documentation and lender approval. Final rate, term and structure are confirmed in a written term sheet.";
  doc.text(doc.splitTextToSize(disclaimer, W - 96), 48, y);
  y += 54;

  doc.setFontSize(9);
  doc.setTextColor(...navy);
  doc.setFont("helvetica", "bold");
  doc.text("Request your written term sheet", 48, y);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...grey);
  doc.text("info@jsgroupholdings.com   ·   WhatsApp +44 7459 103218   ·   jsgroupholdings.com/financing", 48, y + 16);

  doc.save("JS-Aviation-Finance-Estimate.pdf");
}

function Calculator() {
  const [price, setPrice] = useState(1000000);
  const [years, setYears] = useState(7);
  const [rate, setRate] = useState(8.5);

  const down = price * 0.3;
  const financed = price - down;
  const n = years * 12;
  const r = rate / 100 / 12;
  const monthly = r > 0 ? (financed * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : financed / n;
  const total = down + monthly * n;

  return (
    <section data-testid="financing-calculator" className="py-24 md:py-32 border-t border-[#0E1B2E]/10 bg-white">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 text-[#A5854C] mb-6"><div className="hairline w-12"></div>
          <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Payment calculator</span>
        </div>
        <h2 className="font-serif font-light text-[#0E1B2E] text-[36px] md:text-[56px] tracking-[-0.02em] leading-[1.02] max-w-2xl mb-14">
          Run the numbers <span className="italic text-[#A5854C]">before you call.</span>
        </h2>

        <div className="grid grid-cols-12 gap-px bg-[#0E1B2E]/10 border border-[#0E1B2E]/10">
          {/* Inputs */}
          <div className="col-span-12 lg:col-span-7 bg-white p-8 md:p-12 space-y-10">
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE]">Aircraft price</label>
                <span className="font-serif text-[26px] md:text-[32px] text-[#0E1B2E]" data-testid="calc-price-display">{fmtUSD(price)}</span>
              </div>
              <input
                type="range" min="250000" max="20000000" step="50000" value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                data-testid="calc-price-slider"
                className="w-full accent-[#A5854C] h-1 cursor-pointer"
              />
              <div className="flex justify-between mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9AA3AE]">
                <span>$250k</span><span>$20M</span>
              </div>
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE]">Lease term</label>
                <span className="font-serif text-[26px] md:text-[32px] text-[#0E1B2E]" data-testid="calc-term-display">{years} yrs</span>
              </div>
              <input
                type="range" min="3" max="10" step="1" value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                data-testid="calc-term-slider"
                className="w-full accent-[#A5854C] h-1 cursor-pointer"
              />
              <div className="flex justify-between mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9AA3AE]">
                <span>3 yrs</span><span>10 yrs</span>
              </div>
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE]">Indicative rate (p.a.)</label>
                <span className="font-serif text-[26px] md:text-[32px] text-[#0E1B2E]" data-testid="calc-rate-display">{rate.toFixed(1)}%</span>
              </div>
              <input
                type="range" min="5" max="14" step="0.25" value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                data-testid="calc-rate-slider"
                className="w-full accent-[#A5854C] h-1 cursor-pointer"
              />
              <div className="flex justify-between mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9AA3AE]">
                <span>5.0%</span><span>14.0%</span>
              </div>
            </div>
          </div>

          {/* Outputs */}
          <div className="col-span-12 lg:col-span-5 bg-[#F2F2EF] p-8 md:p-12 flex flex-col justify-between gap-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE]">30% down payment</div>
              <div className="mt-2 font-serif text-[40px] md:text-[52px] text-[#A5854C] leading-none" data-testid="calc-down">{fmtUSD(down)}</div>
              <div className="mt-2 text-[12px] text-[#6B7684]">due at closing</div>
            </div>
            <div className="border-t border-[#0E1B2E]/10 pt-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE]">Estimated monthly lease</div>
              <div className="mt-2 font-serif text-[40px] md:text-[52px] text-[#0E1B2E] leading-none" data-testid="calc-monthly">{fmtUSD(monthly)}<span className="text-[18px] text-[#6B7684]"> /mo</span></div>
              <div className="mt-2 text-[12px] text-[#6B7684]">{fmtUSD(financed)} financed over {n} months</div>
            </div>
            <div className="border-t border-[#0E1B2E]/10 pt-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE]">Total cost over term</div>
              <div className="mt-2 font-serif text-[24px] text-[#0E1B2E]" data-testid="calc-total">{fmtUSD(total)}</div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[11px] text-[#9AA3AE] font-mono uppercase tracking-[0.22em] max-w-2xl" data-testid="calc-note">
            Illustrative estimate only — not an offer of credit. Rate slider is adjustable; your confirmed rate is issued in a written term sheet after credit review.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => { downloadEstimatePDF({ price, years, rate, down, financed, monthly, total }); toast.success("Estimate downloaded — ready for your board."); }}
              data-testid="calc-download-pdf"
              className="btn-ghost px-5 py-2.5 text-[10px] uppercase tracking-[0.24em] font-mono inline-flex items-center gap-2"
            >
              <FileDown size={13} /> Download estimate (PDF)
            </button>
            <a href="#finance-form" data-testid="calc-cta" className="btn-gold px-5 py-2.5 text-[10px] uppercase tracking-[0.24em] font-mono inline-flex items-center gap-2">
              Get your real quote <ArrowUpRight size={13} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Financing() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [tsToken, setTsToken] = useState("");
  const widgetRef = useRef(null);
  const widgetId = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let cancelled = false;
    const tryRender = () => {
      if (!cancelled && widgetRef.current && window.turnstile && !widgetId.current) {
        widgetId.current = window.turnstile.render(widgetRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "light",
          appearance: "interaction-only",
          callback: (t) => setTsToken(t),
          "expired-callback": () => setTsToken(""),
          "error-callback": () => setTsToken(""),
        });
      }
    };
    tryRender();
    const iv = setInterval(tryRender, 150);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const [f, setF] = useState({
    name: "", email: "", phone: "", company: "",
    aircraft_target: "", budget_range: "", down_payment_ready: "", residency: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !tsToken) {
      toast.error("Please complete the verification check.");
      return;
    }
    setSubmitting(true);
    const message = [
      `Aircraft target: ${f.aircraft_target || "—"}`,
      `Budget range: ${f.budget_range || "—"}`,
      `30% down payment available: ${f.down_payment_ready || "—"}`,
      `Country of residence / incorporation: ${f.residency || "—"}`,
      f.notes ? `Notes: ${f.notes}` : null,
    ].filter(Boolean).join("\n");
    try {
      await api.post("/contact", {
        name: f.name, email: f.email, phone: f.phone, company: f.company,
        subject: "Aircraft Finance Proposal",
        message,
        turnstile_token: tsToken || undefined,
      });
      toast.success("Thank you. Our finance desk will respond shortly.");
      setDone(true);
      setF({ name: "", email: "", phone: "", company: "", aircraft_target: "", budget_range: "", down_payment_ready: "", residency: "", notes: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setTsToken("");
      if (window.turnstile && widgetId.current) window.turnstile.reset(widgetId.current);
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="page-financing" className="pt-32 min-h-screen bg-[#FAFAF9]">
      {/* Hero */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="flex items-center gap-3 text-[#A5854C] mb-8">
          <div className="hairline w-12"></div>
          <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Financing · Finance Lease</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif font-light tracking-[-0.02em] text-[#0E1B2E] text-[44px] md:text-[72px] lg:text-[88px] leading-[0.98] max-w-5xl"
        >
          Own the aircraft. <span className="italic text-[#A5854C]">Keep your capital working.</span>
        </motion.h1>
        <p className="mt-8 max-w-2xl text-[#6B7684] text-[15px] md:text-[17px] leading-relaxed">
          Through a trusted international aviation finance partner, JS Aviation Group arranges finance-lease structures for new and pre-owned aircraft — from as little as 30% down.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a href="#finance-form" data-testid="financing-cta-form" className="btn-gold px-8 py-4 text-[12px] uppercase tracking-[0.24em] font-mono inline-flex items-center gap-2">
            Request a finance proposal <ArrowUpRight size={14} />
          </a>
          <a href="https://wa.me/447459103218" target="_blank" rel="noreferrer" data-testid="financing-cta-whatsapp" className="btn-ghost px-8 py-4 text-[12px] uppercase tracking-[0.24em] font-mono inline-flex items-center gap-2">
            <MessageCircle size={14} /> WhatsApp +44 7459 103218
          </a>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-[#0E1B2E]/10 bg-[#F2F2EF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            [Percent, "30% down payment", "Conserve capital — finance the remaining 70% over an agreed term."],
            [Landmark, "Indicative, credit-based pricing", "Rates and tenor confirmed in writing, subject to credit review."],
            [BadgeCheck, "Trusted finance partner", "Structures arranged with an established international aviation lender."],
          ].map(([Ic, t, d], i) => (
            <div key={i} className="flex items-start gap-4">
              <Ic className="text-[#A5854C] mt-1 shrink-0" size={20} />
              <div>
                <div className="font-serif text-[20px] text-[#0E1B2E]">{t}</div>
                <div className="mt-1 text-[13px] text-[#6B7684] leading-relaxed max-w-md">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Calculator />

      {/* How it works */}
      <section className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 text-[#A5854C] mb-6"><div className="hairline w-12"></div>
            <span className="font-mono text-[11px] uppercase tracking-[0.35em]">How it works</span>
          </div>
          <h2 className="font-serif font-light text-[#0E1B2E] text-[36px] md:text-[56px] tracking-[-0.02em] leading-[1.02] max-w-2xl mb-14">
            Five steps <span className="italic text-[#A5854C]">to funding.</span>
          </h2>
          <div className="relative">
            <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-[#0E1B2E]/10"></div>
            <div className="space-y-1">
              {steps.map(([n, t, d], i) => (
                <motion.div key={n} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.7, delay: i * 0.05 }}
                  className="relative pl-16 md:pl-24 py-7 border-b border-[#0E1B2E]/5 group" data-testid={`financing-step-${n}`}>
                  <div className="absolute left-3 md:left-5 top-7 w-6 h-6 border border-[#C8A97E]/50 bg-white flex items-center justify-center group-hover:border-[#A5854C] transition-colors">
                    <span className="w-1.5 h-1.5 bg-[#A5854C] rounded-full"></span>
                  </div>
                  <div className="grid grid-cols-12 gap-6 items-start">
                    <span className="col-span-12 md:col-span-1 font-mono text-[11px] uppercase tracking-[0.28em] text-[#9AA3AE]">{n}</span>
                    <h3 className="col-span-12 md:col-span-4 font-serif text-[22px] md:text-[26px] text-[#0E1B2E] tracking-tight leading-tight">{t}</h3>
                    <p className="col-span-12 md:col-span-7 text-[#6B7684] text-[14px] md:text-[15px] leading-relaxed">{d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-[#0E1B2E]/10 py-24 md:py-32 bg-[#F2F2EF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 text-[#A5854C] mb-6"><div className="hairline w-12"></div>
              <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Questions, answered</span>
            </div>
            <h2 className="font-serif font-light text-[#0E1B2E] text-[36px] md:text-[56px] tracking-[-0.02em] leading-[1.02]">
              Straight answers, <span className="italic text-[#A5854C]">in writing.</span>
            </h2>
            <p className="mt-6 text-[#6B7684] text-[15px] leading-relaxed max-w-md">
              Every finance enquiry receives a written term sheet. No verbal indications, no surprises at closing.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 space-y-px bg-[#0E1B2E]/10 border border-[#0E1B2E]/10">
            {faqs.map(([q, a], i) => (
              <div key={i} className="bg-white p-6 md:p-7" data-testid={`financing-faq-${i}`}>
                <h3 className="font-serif text-[18px] md:text-[20px] text-[#0E1B2E] tracking-tight">{q}</h3>
                <p className="mt-2 text-[#6B7684] text-[13px] md:text-[14px] leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Finance enquiry form */}
      <section id="finance-form" className="border-t border-[#0E1B2E]/10 py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 text-[#A5854C] mb-6"><div className="hairline w-12"></div>
            <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Request a proposal</span>
          </div>
          <h2 className="font-serif font-light text-[#0E1B2E] text-[32px] md:text-[48px] tracking-[-0.02em] leading-[1.05] mb-10 max-w-2xl">
            Tell us about the aircraft <span className="italic text-[#A5854C]">and your profile.</span>
          </h2>

          {done ? (
            <div data-testid="finance-success" className="border border-[#C8A97E]/60 bg-[#C8A97E]/10 p-8 md:p-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#A5854C]">Request received</div>
              <h3 className="mt-4 font-serif text-[28px] text-[#0E1B2E] tracking-tight">Thank you — our finance desk will respond shortly.</h3>
              <p className="mt-4 text-[#6B7684] text-[15px] leading-relaxed max-w-lg">Your request has been sent to our team and to info@jsgroupholdings.com. We'll revert with next steps and documentation requirements.</p>
              <button onClick={() => setDone(false)} className="mt-8 btn-ghost px-5 py-2.5 text-[10px] uppercase tracking-[0.24em] font-mono">Submit another request</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="finance-form" className="border border-[#0E1B2E]/10 bg-white p-6 md:p-10">
              <div className="grid grid-cols-2 gap-5">
                <label className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">Aircraft target *</span>
                  <input required data-testid="fin-input-aircraft" value={f.aircraft_target} onChange={upd("aircraft_target")} placeholder="e.g. Tecnam P2010, Challenger 350…" className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none transition-colors" />
                </label>
                <label className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">Budget range (USD)</span>
                  <select data-testid="fin-input-budget" value={f.budget_range} onChange={upd("budget_range")} className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none">
                    <option value="">Select…</option>
                    {["Under 500k", "500k – 1M", "1M – 5M", "5M – 15M", "15M+"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </label>
                <label className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">30% down payment ready?</span>
                  <select data-testid="fin-input-downpayment" value={f.down_payment_ready} onChange={upd("down_payment_ready")} className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none">
                    <option value="">Select…</option>
                    {["Yes — funds ready", "Within 30 days", "Within 90 days", "Exploring"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </label>
                <label className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">Country of residence / incorporation</span>
                  <input data-testid="fin-input-residency" value={f.residency} onChange={upd("residency")} placeholder="e.g. UAE, India, UK…" className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none transition-colors" />
                </label>
                <label className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">Full name *</span>
                  <input required data-testid="fin-input-name" value={f.name} onChange={upd("name")} className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none transition-colors" />
                </label>
                <label className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">Email *</span>
                  <input required type="email" data-testid="fin-input-email" value={f.email} onChange={upd("email")} className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none transition-colors" />
                </label>
                <label className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">Phone (with country code)</span>
                  <input type="tel" data-testid="fin-input-phone" value={f.phone} onChange={upd("phone")} className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none transition-colors" />
                </label>
                <label className="col-span-2 md:col-span-1 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">Company (optional)</span>
                  <input data-testid="fin-input-company" value={f.company} onChange={upd("company")} className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-3 text-[#0E1B2E] text-[14px] outline-none transition-colors" />
                </label>
                <label className="col-span-2 block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-[#9AA3AE] mb-2">Notes (timeline, intended use, questions…)</span>
                  <textarea rows={3} data-testid="fin-input-notes" value={f.notes} onChange={upd("notes")} className="w-full bg-transparent border border-[#0E1B2E]/15 focus:border-[#A5854C] p-3 text-[#0E1B2E] text-[14px] outline-none resize-none transition-colors" />
                </label>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button type="submit" disabled={submitting || (TURNSTILE_SITE_KEY && !tsToken)} data-testid="fin-submit" className="btn-gold px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-mono disabled:opacity-60 inline-flex items-center gap-2">
                  {submitting ? "Submitting…" : "Request a finance proposal"} <ArrowUpRight size={14} />
                </button>
                <div ref={widgetRef} data-testid="fin-turnstile" className="min-h-[2px]"></div>
                <span className="text-[#9AA3AE] text-[12px]">Confidential · no obligation.</span>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="border-t border-[#0E1B2E]/10 py-10 max-w-[1440px] mx-auto px-6 md:px-10 bg-[#FAFAF9]">
        <p className="text-[11px] text-[#9AA3AE] font-mono uppercase tracking-[0.22em] leading-relaxed max-w-4xl" data-testid="financing-disclaimer">
          Financing is provided by an independent third-party lender. JS Aviation Group acts as an introducer only. All offers are indicative, subject to credit review, documentation and lender approval, and do not constitute a binding commitment to lend.
        </p>
      </section>
    </main>
  );
}
